import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Typography, Divider, Stack } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const PRIVACY_POLICY_URL = "https://acttstudy.org/privacy";
const ACCOUNT_DELETION_FORM =
  "https://docs.google.com/forms/d/e/1FAIpQLSdljmVl2AV5tcC9M18iymdqzU4fJuPQEscHcNUfmA_mobYAHQ/viewform?usp=preview&entry.30603203=";

const Support: React.FC = () => {
  const { user } = useAuth0();

  return (
    <>
      {/* React 19 built-in metadata */}
      <title>Support - ACTT Study</title>
      <meta name="description" content="Get help with the ACTT Study app. Contact our support team, view privacy policy, or request account deletion." />
      <meta property="og:title" content="Support - ACTT Study" />
      <meta property="og:description" content="Get help with the ACTT Study app. Contact our support team for assistance." />
      <link rel="canonical" href="https://acttstudy.org/support" />

      <SupportContent user={user} />
    </>
  );
};

const SupportContent: React.FC<{ user: any }> = ({ user }) => {
  // Handlers
  const handlePrivacyPolicy = () => {
    window.open(PRIVACY_POLICY_URL, "_blank", "noopener,noreferrer");
  };

  const handleContactSupport = () => {
    const currentTime = new Date().toLocaleString();
    const subject = encodeURIComponent("ACTT Study App Support Request");
    const body = encodeURIComponent(
      `Name: ${user?.name || "N/A"}\nEmail: ${
        user?.email || "N/A"
      }\nTime: ${currentTime}`
    );
    window.open(
      `mailto:junzhouusc@gmail.com?subject=${subject}&body=${body}`,
      "_blank"
    );
  };

  const handleAccountDeletion = () => {
    window.open(
      `${ACCOUNT_DELETION_FORM}${encodeURIComponent(user?.email || "")}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Box sx={{ maxWidth: 500, mx: "auto", p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Support
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <img
          src="/logo.png"
          alt="ACTT Study Logo"
          style={{ width: 48, height: 48, marginRight: 16 }}
        />
        <Typography variant="h6" component="div">
          Need help? We're here for you!
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        If you have questions, need assistance, or want to request account
        deletion, please use the options below. Our team will respond as soon as
        possible.
      </Typography>
      <Divider sx={{ my: 3 }} />

      <Stack spacing={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DescriptionOutlinedIcon />}
          onClick={handlePrivacyPolicy}
          fullWidth
        >
          Privacy Policy
        </Button>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<HelpOutlineIcon />}
          onClick={handleContactSupport}
          fullWidth
        >
          Contact Support
        </Button>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "red",
            "&:hover": { backgroundColor: "#b71c1c" },
          }}
          startIcon={<DeleteOutlineIcon />}
          onClick={handleAccountDeletion}
          fullWidth
        >
          Request Account Deletion
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default Support;
